import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useState } from 'react';
import { ChatBubbleLeftRightIcon, ChevronDownIcon, EnvelopeIcon, } from '@heroicons/react/24/outline';
import { Button } from '../components/Buttons/Button.js';
import { getMessage } from '../i18n/messages.js';
import { toast } from '../utils/toast.js';
import { useMfaSetupHandlers } from '../hooks/useMfaSetupHandlers.js';
import { PasskeyRegistrationBlock } from './PasskeyRegistrationBlock.js';
import { StepMfaCodeForm, StepMfaPhoneForm } from './RegistrationWizard/index.js';
const MFA_METHOD_OPTIONS = [{ key: 'whatsapp', label: 'WhatsApp', description: 'Send codes via WhatsApp' }];
const OTHER_MFA_METHOD_OPTIONS = [
    { key: 'sms', label: 'Text message', description: 'Send codes via SMS' },
    { key: 'email', label: 'Email', description: 'Send codes to your inbox' },
];
const PRIMARY_MFA_METHOD_OPTION = MFA_METHOD_OPTIONS[0];
const CODE_RESEND_COOLDOWN_SECONDS = 60;
const WhatsAppBrandMark = () => (_jsx("span", { className: "inline-flex h-10 w-10 items-center justify-center", "aria-hidden": "true", children: _jsx("svg", { viewBox: "0 0 24 24", className: "h-9 w-9 text-[#25D366]", fill: "currentColor", children: _jsx("path", { d: "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.946L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" }) }) }));
const OtherMethodIcon = ({ method }) => {
    const Icon = method === 'email' ? EnvelopeIcon : ChatBubbleLeftRightIcon;
    return (_jsx("span", { className: "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-700", "aria-hidden": "true", children: _jsx(Icon, { className: "h-4 w-4" }) }));
};
const getInitialStep = (method, alreadyEnrolled) => {
    if (method === 'email' || (alreadyEnrolled && method === 'sms')) {
        return 'code';
    }
    return 'phone';
};
const getMethodLabel = (method) => {
    if (method === 'whatsapp')
        return 'WhatsApp';
    if (method === 'email')
        return 'Email';
    return 'SMS';
};
const getCodeDeliveryLabel = (method) => {
    if (method === 'email')
        return 'email';
    if (method === 'whatsapp')
        return 'WhatsApp';
    return 'SMS';
};
export const MfaSetupFlow = ({ userId, email = '', tenantId, alreadyEnrolled = false, enabledMethods, onVerified, defaultMethod = 'whatsapp', requireTenantForWhatsapp = false, }) => {
    const [serverError, setServerError] = useState(null);
    const [selectedMethod, setSelectedMethod] = useState(defaultMethod);
    const [otherMethodsOpen, setOtherMethodsOpen] = useState(defaultMethod === 'sms' || defaultMethod === 'email');
    const [forcePhoneRegistration, setForcePhoneRegistration] = useState(false);
    const [requestingExistingCode, setRequestingExistingCode] = useState(false);
    const [emailCodeSent, setEmailCodeSent] = useState(false);
    const [emailCodeSentAt, setEmailCodeSentAt] = useState(null);
    const [codeError, setCodeError] = useState(null);
    const [pendingPhoneDto, setPendingPhoneDto] = useState(null);
    const [resendAvailableAt, setResendAvailableAt] = useState(null);
    const [resendTick, setResendTick] = useState(0);
    const hasMethodSpecificEnrollment = Array.isArray(enabledMethods) && enabledMethods.length > 0;
    const isMethodEnrolled = useCallback((method) => hasMethodSpecificEnrollment ? enabledMethods.includes(method) : alreadyEnrolled, [alreadyEnrolled, enabledMethods, hasMethodSpecificEnrollment]);
    const selectedMethodEnrolled = isMethodEnrolled(selectedMethod);
    const shouldReuseExistingMfa = selectedMethodEnrolled && !forcePhoneRegistration && selectedMethod === 'sms';
    const shouldShowEnabledMethodStatus = selectedMethodEnrolled && !forcePhoneRegistration && selectedMethod !== 'sms';
    const [step, setStep] = useState(() => getInitialStep(defaultMethod, alreadyEnrolled));
    const { submitPhone, submitCode, requestExistingMfaCode, registeringMfa, verifyingMfa } = useMfaSetupHandlers({
        userId,
        onServerError: setServerError,
        alreadyEnrolled: shouldReuseExistingMfa,
        method: selectedMethod,
        email,
        tenantId,
    });
    const resendCooldownSeconds = resendAvailableAt
        ? Math.max(0, Math.ceil((resendAvailableAt.getTime() - Date.now()) / 1000))
        : 0;
    const startResendCooldown = useCallback(() => {
        setResendAvailableAt(new Date(Date.now() + CODE_RESEND_COOLDOWN_SECONDS * 1000));
        setResendTick((tick) => tick + 1);
    }, []);
    useEffect(() => {
        if (shouldShowEnabledMethodStatus) {
            setStep('code');
            return;
        }
        if (selectedMethod === 'email') {
            setStep('code');
            return;
        }
        if (shouldReuseExistingMfa) {
            setStep('code');
            return;
        }
        if (pendingPhoneDto) {
            setStep('code');
            return;
        }
        setStep('phone');
    }, [pendingPhoneDto, selectedMethod, shouldReuseExistingMfa, shouldShowEnabledMethodStatus]);
    useEffect(() => {
        if (selectedMethod !== 'email') {
            setEmailCodeSent(false);
            setEmailCodeSentAt(null);
        }
    }, [selectedMethod]);
    useEffect(() => {
        if (!resendAvailableAt || resendCooldownSeconds <= 0) {
            return;
        }
        const timer = window.setTimeout(() => setResendTick((tick) => tick + 1), 1000);
        return () => window.clearTimeout(timer);
    }, [resendAvailableAt, resendCooldownSeconds, resendTick]);
    const selectMethod = useCallback((method) => {
        const methodEnrolled = isMethodEnrolled(method);
        setSelectedMethod(method);
        setForcePhoneRegistration(false);
        setServerError(null);
        setCodeError(null);
        setPendingPhoneDto(null);
        setResendAvailableAt(null);
        if (method !== 'email') {
            setEmailCodeSent(false);
            setEmailCodeSentAt(null);
        }
        if (method === 'sms' || method === 'email') {
            setOtherMethodsOpen(true);
        }
        setStep(method === 'email' || methodEnrolled ? 'code' : 'phone');
    }, [isMethodEnrolled]);
    const sendExistingCode = useCallback(async (options) => {
        const result = await requestExistingMfaCode();
        if (result?.ok) {
            if (options?.showToast !== false) {
                toast.success(getMessage('en-US', 'mfaInitiatedSuccess'));
            }
            startResendCooldown();
            setStep('code');
            return result;
        }
        if (result?.reason === 'missing-user') {
            toast.error(getMessage('en-US', 'loginMfaMissingUser'));
            return result;
        }
        toast.error(options?.failureMessage ?? getMessage('en-US', 'mfaPhoneError'));
        return (result ?? {
            ok: false,
            reason: 'error',
            error: new Error('missing-mfa-response'),
        });
    }, [requestExistingMfaCode, startResendCooldown]);
    const handlePhoneSubmit = useCallback(async (dto) => {
        const result = await submitPhone(dto);
        if (result.ok) {
            toast.success(getMessage('en-US', 'mfaInitiatedSuccess'));
            setPendingPhoneDto(dto);
            setCodeError(null);
            startResendCooldown();
            setStep('code');
            return;
        }
        if (result.reason === 'missing-user') {
            toast.error(getMessage('en-US', 'loginMfaMissingUser'));
            return;
        }
        const errorMsg = selectedMethod === 'email'
            ? getMessage('en-US', 'genericError')
            : selectedMethod === 'whatsapp'
                ? 'Could not send WhatsApp code.'
                : getMessage('en-US', 'mfaPhoneError');
        toast.error(errorMsg);
    }, [selectedMethod, startResendCooldown, submitPhone]);
    const handleCodeSubmit = async (code) => {
        setCodeError(null);
        if ((!selectedMethodEnrolled || forcePhoneRegistration) &&
            selectedMethod !== 'email' &&
            !pendingPhoneDto) {
            const label = getCodeDeliveryLabel(selectedMethod);
            setCodeError(`Send a ${label} code before verifying this method.`);
            setStep('phone');
            return;
        }
        if (!selectedMethodEnrolled && selectedMethod === 'email' && !emailCodeSent) {
            setCodeError('Send an email code before verifying this method.');
            return;
        }
        const result = await submitCode(code);
        if (result.ok) {
            toast.success(getMessage('en-US', 'mfaVerifiedSuccess'));
            await onVerified();
            return;
        }
        if (result.reason === 'missing-user') {
            const message = getMessage('en-US', 'mfaCodeError');
            setCodeError(message);
            toast.error(message);
            return;
        }
        const error = result.error;
        if (error && 'response' in error) {
            const response = error.response;
            const codeValue = response?.data?.code;
            if (codeValue === 'otp-invalid') {
                const message = `That ${getCodeDeliveryLabel(selectedMethod)} code was not accepted. Check the latest code and try again.`;
                setCodeError(message);
                toast.error(message);
                return;
            }
            if (codeValue === 'otp-expired') {
                const message = 'That code has expired. Request a new code and try again.';
                setCodeError(message);
                toast.error(message);
                return;
            }
            if (codeValue === 'otp-rate-limited') {
                const message = 'Too many attempts. Please wait and try again.';
                setCodeError(message);
                toast.error(message);
                return;
            }
            if (response?.status === 400 || response?.status === 401 || response?.status === 403) {
                const message = `That ${getCodeDeliveryLabel(selectedMethod)} code was not accepted. Check the latest code and try again.`;
                setCodeError(message);
                toast.error(message);
                return;
            }
            if (response?.status === 410 || response?.status === 422) {
                const message = 'That code has expired. Request a new code and try again.';
                setCodeError(message);
                toast.error(message);
                return;
            }
            if (response?.status === 429) {
                const message = 'Too many attempts. Please wait and try again.';
                setCodeError(message);
                toast.error(message);
                return;
            }
            if (response?.status && response.status >= 500) {
                const message = `${getMethodLabel(selectedMethod)} verification is temporarily unavailable. Please try again in a minute.`;
                setCodeError(message);
                toast.error(message);
                return;
            }
            const message = response?.data?.message;
            if (typeof message === 'string' && message.trim().length > 0) {
                setCodeError(message);
                toast.error(message);
                return;
            }
        }
        const message = getMessage('en-US', 'mfaCodeError');
        setCodeError(message);
        toast.error(message);
    };
    const handleUseDifferentNumber = useCallback(() => {
        setForcePhoneRegistration(true);
        setServerError(null);
        setCodeError(null);
        setPendingPhoneDto(null);
        setResendAvailableAt(null);
        setStep('phone');
    }, []);
    const handleRestoreSavedNumber = useCallback(() => {
        setForcePhoneRegistration(false);
        setServerError(null);
        setCodeError(null);
        setPendingPhoneDto(null);
        setResendAvailableAt(null);
        setStep('code');
    }, []);
    const handleEmailRequest = useCallback(async () => {
        const result = await submitPhone({ phoneNumber: '', secret: undefined });
        if (result.ok) {
            toast.success(getMessage('en-US', 'mfaInitiatedSuccess'));
            setEmailCodeSent(true);
            setEmailCodeSentAt(new Date());
            setCodeError(null);
            startResendCooldown();
            setStep('code');
            return;
        }
        setEmailCodeSent(false);
        setEmailCodeSentAt(null);
        if (result.reason === 'missing-user') {
            toast.error(getMessage('en-US', 'loginMfaMissingUser'));
            return;
        }
        const errorMsg = selectedMethod === 'email'
            ? getMessage('en-US', 'genericError')
            : selectedMethod === 'whatsapp'
                ? 'Could not send WhatsApp code.'
                : getMessage('en-US', 'mfaPhoneError');
        toast.error(errorMsg);
    }, [selectedMethod, startResendCooldown, submitPhone]);
    const handleResendCode = useCallback(() => {
        if (requestingExistingCode || resendCooldownSeconds > 0) {
            return;
        }
        setRequestingExistingCode(true);
        const request = pendingPhoneDto
            ? handlePhoneSubmit(pendingPhoneDto)
            : shouldReuseExistingMfa
                ? sendExistingCode()
                : selectedMethod === 'email'
                    ? handleEmailRequest()
                    : Promise.resolve({ ok: false, reason: 'missing-user' });
        void request.finally(() => setRequestingExistingCode(false));
    }, [
        handleEmailRequest,
        handlePhoneSubmit,
        pendingPhoneDto,
        requestingExistingCode,
        resendCooldownSeconds,
        selectedMethod,
        sendExistingCode,
        shouldReuseExistingMfa,
    ]);
    const renderResendCodeControl = () => {
        const canRequestCode = shouldReuseExistingMfa || selectedMethod === 'email' || pendingPhoneDto !== null;
        if (!canRequestCode) {
            return null;
        }
        if (resendCooldownSeconds > 0) {
            return (_jsxs("p", { className: "text-center text-sm text-gray-500", "aria-live": "polite", children: ["Send new code in ", resendCooldownSeconds, "s"] }));
        }
        return (_jsx(Button, { htmlType: "button", variant: "text", className: "mx-auto", disabled: registeringMfa, loading: requestingExistingCode, onClick: handleResendCode, children: resendAvailableAt ? 'Send new code' : 'Send code' }));
    };
    const renderExistingMfaBanner = () => {
        if (!selectedMethodEnrolled || forcePhoneRegistration) {
            return null;
        }
        if (selectedMethod !== 'sms') {
            const label = getMethodLabel(selectedMethod);
            const changeLabel = selectedMethod === 'whatsapp'
                ? 'Use a different WhatsApp number'
                : selectedMethod === 'email'
                    ? 'Use a different email address'
                    : getMessage('en-US', 'mfaUseDifferentNumber');
            return (_jsxs("div", { className: "rounded-md border border-primary/20 bg-primary/5 p-4 space-y-3 text-sm text-gray-700", children: [_jsxs("div", { children: [_jsxs("p", { className: "font-semibold", children: [label, " MFA already enabled"] }), _jsxs("p", { children: [label, " is active on your account."] })] }), _jsx("button", { type: "button", className: "text-sm text-gray-600 underline", onClick: handleUseDifferentNumber, children: changeLabel })] }));
        }
        return (_jsxs("div", { className: "rounded-md border border-primary/20 bg-primary/5 p-4 space-y-3 text-sm text-gray-700", children: [_jsxs("div", { children: [_jsx("p", { className: "font-semibold", children: getMessage('en-US', 'mfaExistingEnrollmentTitle') }), _jsx("p", { children: "Request an SMS to your saved number, then enter it below." })] }), _jsxs("div", { className: "flex flex-wrap items-center gap-3", children: [renderResendCodeControl(), _jsx("button", { type: "button", className: "text-sm text-gray-600 underline", onClick: handleUseDifferentNumber, children: getMessage('en-US', 'mfaUseDifferentNumber') })] })] }));
    };
    const renderChangeNumberHint = () => {
        if (!alreadyEnrolled || !forcePhoneRegistration) {
            return null;
        }
        return (_jsxs("div", { className: "rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600", children: [_jsx("p", { children: getMessage('en-US', 'mfaChangingNumberMessage') }), _jsx("button", { type: "button", className: "mt-2 text-primary underline", onClick: handleRestoreSavedNumber, children: getMessage('en-US', 'mfaUseSavedNumber') })] }));
    };
    const renderStep = () => {
        if (shouldShowEnabledMethodStatus) {
            return null;
        }
        if (selectedMethod === 'email') {
            return (_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700", children: [_jsx("p", { className: "font-semibold", children: "Receive codes via email" }), _jsxs("p", { children: ["We will send a one-time code to", ' ', _jsx("span", { className: "font-medium", children: email || 'your account email' }), ". Enter it below to finish enabling email MFA."] })] }), emailCodeSent ? (_jsxs("div", { className: "rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800", "data-testid": "email-code-sent-banner", children: [_jsx("p", { className: "font-semibold", children: "Verification email sent" }), _jsxs("p", { children: ["Check ", email || 'your inbox', " for your code.", ' ', emailCodeSentAt ? `Sent at ${emailCodeSentAt.toLocaleTimeString()}.` : ''] })] })) : null, _jsx(Button, { htmlType: "button", variant: "primary", className: "w-full", loading: registeringMfa, onClick: handleEmailRequest, children: "Send code to email" }), _jsx(StepMfaCodeForm, { onVerify: handleCodeSubmit, loading: verifyingMfa }), codeError ? (_jsx("div", { className: "rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800", role: "alert", children: codeError })) : null, renderResendCodeControl()] }));
        }
        if (step === 'phone') {
            return (_jsxs(_Fragment, { children: [renderChangeNumberHint(), _jsx(StepMfaPhoneForm, { onNext: handlePhoneSubmit, loading: registeringMfa, serverError: serverError, method: selectedMethod, showMethodOptions: false, heading: selectedMethod === 'whatsapp' ? getMessage('en-US', 'mfaWhatsappHeading') : undefined, label: selectedMethod === 'whatsapp' ? 'WhatsApp Number' : 'Phone Number', placeholder: selectedMethod === 'whatsapp' ? '+27123456789' : 'Enter mobile number', ctaText: selectedMethod === 'whatsapp' ? 'Send WhatsApp code' : undefined, deliveryAvailable: !requireTenantForWhatsapp || selectedMethod !== 'whatsapp' || Boolean(tenantId), unavailableMessage: "WhatsApp MFA is unavailable until your account finishes linking. Choose SMS or email instead." })] }));
        }
        return (_jsxs("div", { className: "space-y-3", children: [_jsx(StepMfaCodeForm, { onVerify: handleCodeSubmit, loading: verifyingMfa }), codeError ? (_jsx("div", { className: "rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800", role: "alert", children: codeError })) : null, !shouldReuseExistingMfa ? renderResendCodeControl() : null, shouldReuseExistingMfa && (_jsx("button", { type: "button", className: "text-sm text-gray-600 underline", onClick: handleUseDifferentNumber, children: getMessage('en-US', 'mfaUseDifferentNumber') }))] }));
    };
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "mx-auto w-full max-w-md space-y-3", role: "radiogroup", "aria-label": "MFA delivery method", "data-testid": "mfa-delivery-methods", children: [_jsxs("label", { className: `block w-full cursor-pointer rounded-md border p-4 text-center transition focus-within:ring-2 focus-within:ring-primary ${selectedMethod === PRIMARY_MFA_METHOD_OPTION.key
                            ? 'border-[#25D366] bg-[#25D366]/10 shadow-sm'
                            : 'border-primary/40 bg-white hover:border-primary'}`, "data-testid": "primary-mfa-method", children: [_jsx("input", { type: "radio", name: "mfa-delivery-method", value: PRIMARY_MFA_METHOD_OPTION.key, checked: selectedMethod === PRIMARY_MFA_METHOD_OPTION.key, onChange: () => selectMethod(PRIMARY_MFA_METHOD_OPTION.key), className: "sr-only" }), _jsxs("span", { className: "flex items-center gap-3 text-left", children: [_jsx(WhatsAppBrandMark, {}), _jsxs("span", { className: "text-left", children: [_jsx("span", { className: "block text-[11px] font-semibold uppercase tracking-wide text-[#128C7E]", children: "Official WhatsApp" }), _jsx("span", { className: "block font-semibold text-gray-900", children: PRIMARY_MFA_METHOD_OPTION.label }), _jsx("span", { className: "block text-sm text-gray-600", children: PRIMARY_MFA_METHOD_OPTION.description })] })] })] }), _jsxs("div", { children: [_jsxs("button", { type: "button", className: "mx-auto flex items-center justify-center gap-1 rounded-full border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-600 shadow-sm transition hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2", "aria-expanded": otherMethodsOpen, "aria-controls": "other-mfa-methods", onClick: () => setOtherMethodsOpen((open) => !open), children: ["Other methods", _jsx(ChevronDownIcon, { className: `h-3.5 w-3.5 transition-transform ${otherMethodsOpen ? 'rotate-180' : ''}`, "aria-hidden": "true" })] }), otherMethodsOpen ? (_jsx("div", { id: "other-mfa-methods", className: "mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2", children: OTHER_MFA_METHOD_OPTIONS.map((option) => {
                                    const isActive = selectedMethod === option.key;
                                    return (_jsxs("label", { className: `cursor-pointer rounded-md border p-4 text-left transition focus-within:ring-2 focus-within:ring-primary ${isActive
                                            ? 'border-primary bg-primary/5 shadow-sm'
                                            : 'border-gray-200 bg-white'}`, children: [_jsx("input", { type: "radio", name: "mfa-delivery-method", value: option.key, checked: isActive, onChange: () => selectMethod(option.key), className: "sr-only" }), _jsxs("span", { className: "flex items-start gap-3", children: [_jsx(OtherMethodIcon, { method: option.key }), _jsxs("span", { children: [_jsx("span", { className: "block font-semibold", children: option.label }), _jsx("span", { className: "block text-sm text-gray-600", children: option.description })] })] })] }, option.key));
                                }) })) : null] })] }), _jsx("div", { className: "mx-auto w-full max-w-md", children: _jsx(PasskeyRegistrationBlock, {}) }), renderExistingMfaBanner(), renderStep()] }));
};
//# sourceMappingURL=MfaSetupFlow.js.map